import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import AuthorByline from "@/components/AuthorByline";
import { buildBreadcrumb, buildService } from "@/lib/seo/schemas";
import PaymentDialog from "@/components/PaymentDialog";
import {
  Briefcase, Users, GraduationCap, Palette, Crown, Wallet,
  CheckCircle, Gift, Clock, ChevronRight, Sparkles, Star, ShieldCheck, Lock
} from "lucide-react";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
});

interface PackageItem {
  icon: React.ReactNode;
  badge: string;
  title: string;
  subtitle: string;
  focus: string;
  duration: string;
  includes: { label: string; value: string }[];
  freebie: string;
  totalValue: string;
  price: string;
  /** numeric INR amount for Razorpay; omit for "On Request" packages */
  amount?: number;
  priceNote?: string;
  featured?: boolean;
  tier: "standard" | "premium" | "elite";
}

const packages: PackageItem[] = [
  {
    icon: <Briefcase className="h-6 w-6" />,
    badge: "For Professionals",
    title: "The Executive Edge",
    subtitle: "Life Coaching for Working Professionals",
    focus: "Navigating life & career stagnation, leadership transitions, and internal roadblocks.",
    duration: "3 Months · 6 Fortnightly Sessions",
    includes: [
      { label: "360° Professional Audit", value: "₹15,000" },
      { label: "6 One-on-One ICF-NLP Coaching Sessions", value: "₹60,000" },
      { label: "Unlimited WhatsApp Support for decision-making", value: "₹10,000" },
    ],
    freebie: 'A personalized Graphology Report to identify "Success Blockers"',
    totalValue: "₹85,000",
    price: "₹65,000",
    amount: 65000,
    tier: "standard",
  },
  {
    icon: <Users className="h-6 w-6" />,
    badge: "For Teams",
    title: "The Corporate Catalyst",
    subtitle: "Soft Skills Training for High-Performance Teams",
    focus: "Communication, Emotional Intelligence, and Conflict Resolution for mid-senior levels.",
    duration: "2-Day Intensive · 4 Hours Each",
    includes: [
      { label: "Pre-Training Needs Assessment", value: "₹10,000" },
      { label: "Custom Curriculum Design", value: "₹15,000" },
      { label: "In-person/Virtual Workshop Delivery", value: "₹50,000" },
      { label: "Post-Session Action Plan", value: "₹5,000" },
    ],
    freebie: "One 60-min Group Debrief + coaching for 4 selected participants",
    totalValue: "₹80,000",
    price: "₹60,000",
    amount: 60000,
    priceNote: "+ Logistics",
    featured: true,
    tier: "premium",
  },
  {
    icon: <GraduationCap className="h-6 w-6" />,
    badge: "For Students",
    title: "The Future-Ready Blueprint",
    subtitle: "Campus-to-Corporate Transition",
    focus: "Mastering unwritten rules, interview mastery, and professional presence.",
    duration: "2-Day Boot Camp · 4 Sessions",
    includes: [
      { label: "Mock Interviews with Feedback", value: "₹10,000" },
      { label: "Email & Digital Etiquette Workshop", value: "₹7,000" },
      { label: "Resume & LinkedIn Architecture", value: "₹8,000" },
      { label: "Confidence Building via NLP", value: "₹10,000" },
    ],
    freebie: 'Lifetime access to the MVIBE Corporate Checklist — "The First Monday" Guide',
    totalValue: "₹45,000",
    price: "On Request",
    priceNote: "Group rates available for colleges (60+ students)",
    tier: "standard",
  },
  {
    icon: <Palette className="h-6 w-6" />,
    badge: "Wellness",
    title: "The Somatic Reset",
    subtitle: "Stress, AI-Anxiety & Burnout Management",
    focus: "Using Art Therapy and Graphology to process the heavy energy of the modern tech world.",
    duration: "6 Weeks · Weekly 90-min Sessions",
    includes: [
      { label: "Initial Somatic Assessment", value: "₹5,000" },
      { label: "3 Art Therapy Sessions", value: "₹21,000" },
      { label: "2 NLP Mindset-Reset Sessions", value: "₹20,000" },
      { label: "Graphology-based Behavior Modification Plan", value: "₹10,000" },
    ],
    freebie: "A Premium Digital Art Kit (physical kit on additional cost)",
    totalValue: "₹56,000",
    price: "₹45,000",
    amount: 45000,
    tier: "standard",
  },
  {
    icon: <Crown className="h-6 w-6" />,
    badge: "Ultra-Premium",
    title: "The Mahirah Signature",
    subtitle: "The Bespoke Elite Life-Design",
    focus: "A 360° transformation — high luxury, high privacy, absolute impact. The \"Butler Service\" of coaching.",
    duration: "6 Months · Retainer Model",
    includes: [
      { label: "Bi-weekly 1:1 Sessions (Coaching + Art Therapy + Graphology)", value: "₹2,40,000" },
      { label: "Emergency On-Call Support for critical events", value: "₹1,00,000" },
      { label: "Image Consulting & Soft Skills for public appearances", value: "₹50,000" },
      { label: "Private Quarterly Retreat (Art & Mindfulness)", value: "₹60,000" },
    ],
    freebie: "A custom Watercolor Series of your journey, painted by Mahirah during sessions",
    totalValue: "₹6,50,000+",
    price: "₹4,50,000",
    amount: 450000,
    priceNote: "Full Retainer",
    featured: true,
    tier: "elite",
  },
];

const tierStyles = {
  standard: {
    card: "bg-card border-border",
    badge: "bg-muted text-muted-foreground",
    icon: "bg-primary/10 text-primary",
    priceLabel: "text-primary",
    button: "default" as const,
  },
  premium: {
    card: "bg-card border-secondary/30 shadow-lg shadow-secondary/5",
    badge: "bg-secondary text-secondary-foreground",
    icon: "bg-secondary/10 text-secondary",
    priceLabel: "text-secondary",
    button: "default" as const,
  },
  elite: {
    card: "bg-gradient-to-br from-primary via-primary to-secondary border-accent/40 text-primary-foreground shadow-xl shadow-primary/20",
    badge: "bg-accent text-accent-foreground",
    icon: "bg-accent/20 text-accent",
    priceLabel: "text-accent",
    button: "secondary" as const,
  },
};

const PackageCard = ({ pkg, index }: { pkg: PackageItem; index: number }) => {
  const style = tierStyles[pkg.tier];
  const isElite = pkg.tier === "elite";
  const [payOpen, setPayOpen] = useState(false);
  const canPay = typeof pkg.amount === "number" && pkg.amount >= 100;

  const handleCta = () => {
    if (canPay) {
      setPayOpen(true);
      return;
    }
    const subject = encodeURIComponent(`Enquiry: ${pkg.title} Package`);
    const body = encodeURIComponent(
      `Hi MVIBE,\n\nI'm interested in the "${pkg.title}" package. Please share more details.\n\nThank you.`,
    );
    window.location.href = `mailto:hello@mvibeon.com?subject=${subject}&body=${body}`;
  };

  return (
    <motion.div
      {...fadeUp(index * 0.1)}
      className={`relative rounded-3xl border p-8 md:p-10 flex flex-col ${style.card} transition-all duration-300 hover:shadow-xl hover:-translate-y-1`}
    >
      {pkg.featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-4 py-1 text-xs font-bold text-accent-foreground shadow-md">
            <Star className="h-3 w-3 fill-current" /> Most Popular
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <div className={`flex-shrink-0 rounded-2xl p-3 ${style.icon}`}>
          {pkg.icon}
        </div>
        <div className="flex-1">
          <span className={`inline-block rounded-full px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wider mb-2 ${style.badge}`}>
            {pkg.badge}
          </span>
          <h2 className={`text-2xl font-heading font-bold ${isElite ? "text-primary-foreground" : "text-foreground"}`}>
            {pkg.title}
          </h2>
          <p className={`text-sm mt-0.5 ${isElite ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
            {pkg.subtitle}
          </p>
        </div>
      </div>

      {/* Focus */}
      <p className={`text-sm leading-relaxed mb-6 ${isElite ? "text-primary-foreground/85" : "text-muted-foreground"}`}>
        {pkg.focus}
      </p>

      {/* Duration */}
      <div className={`flex items-center gap-2 text-sm font-medium mb-6 ${isElite ? "text-accent" : "text-foreground"}`}>
        <Clock className="h-4 w-4" />
        {pkg.duration}
      </div>

      {/* Includes */}
      <div className="flex-1 mb-6">
        <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isElite ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
          What's Included
        </p>
        <ul className="space-y-2.5">
          {pkg.includes.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <CheckCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isElite ? "text-accent" : "text-secondary"}`} />
              <span className={`text-sm leading-snug ${isElite ? "text-primary-foreground/90" : "text-foreground"}`}>
                {item.label}
                <span className={`ml-1 text-xs ${isElite ? "text-primary-foreground/50" : "text-muted-foreground"}`}>
                  ({item.value})
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Freebie */}
      <div className={`rounded-xl p-4 mb-6 ${isElite ? "bg-accent/10 border border-accent/20" : "bg-muted/50 border border-border"}`}>
        <div className="flex items-start gap-2">
          <Gift className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isElite ? "text-accent" : "text-accent"}`} />
          <div>
            <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isElite ? "text-accent" : "text-accent-foreground"}`}>
              Bonus Freebie
            </p>
            <p className={`text-sm leading-snug ${isElite ? "text-primary-foreground/85" : "text-foreground"}`}>
              {pkg.freebie}
            </p>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className={`border-t pt-6 mb-6 ${isElite ? "border-primary-foreground/15" : "border-border"}`}>
        <div className="flex items-baseline justify-between">
          <div>
            <p className={`text-xs line-through ${isElite ? "text-primary-foreground/40" : "text-muted-foreground"}`}>
              Value: {pkg.totalValue}
            </p>
            <p className={`text-3xl font-heading font-bold ${style.priceLabel}`}>
              {pkg.price}
            </p>
            {pkg.priceNote && (
              <p className={`text-xs mt-0.5 ${isElite ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                {pkg.priceNote}
              </p>
            )}
          </div>
          {pkg.totalValue !== pkg.price && pkg.price !== "On Request" && (
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${isElite ? "bg-accent/20 text-accent" : "bg-secondary/15 text-secondary"}`}>
              Save {(() => {
                const total = parseInt(pkg.totalValue.replace(/[₹,+]/g, ""));
                const price = parseInt(pkg.price.replace(/[₹,+]/g, ""));
                return Math.round(((total - price) / total) * 100);
              })()}%
            </span>
          )}
        </div>
      </div>

      {/* CTA */}
      <Button
        variant={style.button}
        size="lg"
        className={`w-full rounded-xl text-base font-semibold ${isElite ? "bg-accent text-accent-foreground hover:bg-accent/90" : ""}`}
        onClick={handleCta}
      >
        {canPay ? (
          <>
            <Lock className="h-4 w-4 mr-2" />
            Pay {pkg.price} Now
          </>
        ) : (
          <>
            Request Pricing
            <ChevronRight className="h-4 w-4 ml-1" />
          </>
        )}
      </Button>

      {canPay && (
        <p className={`mt-3 flex items-center justify-center gap-1.5 text-[11px] ${isElite ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
          <ShieldCheck className="h-3 w-3" />
          Secure payment via Razorpay
        </p>
      )}

      {canPay && (
        <PaymentDialog
          open={payOpen}
          onOpenChange={setPayOpen}
          packageTitle={pkg.title}
          amount={pkg.amount}
        />
      )}
    </motion.div>
  );
};

const CustomPaymentCard = ({ index }: { index: number }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      {...fadeUp(index * 0.1)}
      className="relative rounded-3xl border-2 border-dashed border-accent/40 bg-gradient-to-br from-card via-card to-accent/5 p-8 md:p-10 flex flex-col items-center text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-accent/70"
    >
      <div className="rounded-2xl bg-accent/15 p-4 mb-5">
        <Wallet className="h-8 w-8 text-accent" />
      </div>
      <span className="inline-block rounded-full bg-accent/15 text-accent px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wider mb-3">
        Tailored
      </span>
      <h3 className="text-2xl font-heading font-bold text-foreground mb-3">
        Custom Payment
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed mb-6 max-w-sm">
        Already discussed a tailored programme with Mahirah? Enter your agreed amount and complete your payment securely in seconds.
      </p>
      <ul className="text-sm text-foreground/80 space-y-2 mb-8 text-left w-full max-w-xs">
        <li className="flex items-start gap-2">
          <CheckCircle className="h-4 w-4 mt-0.5 text-accent flex-shrink-0" />
          Pay any agreed amount
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle className="h-4 w-4 mt-0.5 text-accent flex-shrink-0" />
          Add your reference / purpose
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle className="h-4 w-4 mt-0.5 text-accent flex-shrink-0" />
          Instant payment receipt
        </li>
      </ul>
      <Button
        size="lg"
        className="w-full rounded-xl text-base font-semibold bg-accent text-accent-foreground hover:bg-accent/90"
        onClick={() => setOpen(true)}
      >
        <Lock className="h-4 w-4 mr-2" />
        Make a Custom Payment
      </Button>
      <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
        <ShieldCheck className="h-3 w-3" />
        Secure payment via Razorpay
      </p>
      <PaymentDialog
        open={open}
        onOpenChange={setOpen}
        packageTitle="Custom Payment"
        isCustom
      />
    </motion.div>
  );
};

const Packages = () => (
  <>
    <SEOHead
      title="Executive Coaching & Corporate Training Packages Dubai"
      description="Transparent coaching and corporate training packages in Dubai. Compare tiers, inclusions and pricing for individuals, teams and student cohorts."
      canonical="https://mvibeon.com/packages"
      schema={[
        buildService({
          name: "MVIBE Coaching and Training Packages",
          description: "Structured packages for 1:1 coaching, corporate training and campus programmes with transparent inclusions and delivery options.",
          path: "/packages",
          serviceType: "Coaching packages",
        }),
        buildBreadcrumb([
          { name: "Home", path: "/" },
          { name: "Packages", path: "/packages" },
        ]),
      ]}
    />

    {/* Hero */}
    <section className="relative overflow-hidden bg-primary py-20 md:py-28">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-accent blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 h-48 w-48 rounded-full bg-secondary blur-[80px]" />
      </div>
      <div className="container relative mx-auto px-4 text-center">
        <motion.div {...fadeUp()}>
          <nav className="mb-6 text-sm text-primary-foreground/60">
            <Link to="/" className="hover:text-primary-foreground transition-colors">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-primary-foreground">Packages</span>
          </nav>
          <div className="inline-flex items-center gap-2 mb-4 rounded-full bg-accent/15 px-4 py-1.5 text-accent text-sm font-medium">
            <Sparkles className="h-4 w-4" /> Curated Transformation Programs
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-primary-foreground max-w-3xl mx-auto leading-tight">
            Choose Your Path to Growth
          </h1>
          <p className="mt-5 text-lg text-primary-foreground/70 max-w-2xl mx-auto">
            Five distinct packages designed for professionals, teams, students, and visionaries — each crafted to unlock your next level.
          </p>
        </motion.div>
      </div>
    </section>

    {/* Packages Grid */}
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* First 3 cards */}
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 mb-8">
          {packages.slice(0, 3).map((pkg, i) => (
            <PackageCard key={pkg.title} pkg={pkg} index={i} />
          ))}
        </div>
        {/* Last 2 cards + Custom Payment */}
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {packages.slice(3).map((pkg, i) => (
            <PackageCard key={pkg.title} pkg={pkg} index={i + 3} />
          ))}
          <CustomPaymentCard index={5} />
        </div>
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="py-16 bg-muted">
      <div className="container mx-auto px-4 text-center">
        <motion.div {...fadeUp()}>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Not Sure Which Package Fits?
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">
            Book a free 15-minute discovery call and we'll help you find the perfect program for your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="rounded-xl" asChild>
              <Link to="/contact">Book a Discovery Call</Link>
            </Button>
            <Button variant="outline" size="lg" className="rounded-xl" asChild>
              <Link to="/services">Explore All Services</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>

    <section className="bg-background py-10">
      <div className="container mx-auto px-4">
        <AuthorByline />
      </div>
    </section>
  </>
);

export default Packages;
